import { useState } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { DataItem, connect, navigateTo } from 'scrivito'

import { Badge, Variant as BadgeVariant } from '@/Atomic/Atoms/Badge'
import { DataItemLink } from '@/Atomic/Atoms/DataItemLink'
import { Icon } from '@/Atomic/Atoms/Icon'
import { StringText } from '@/Atomic/Atoms/StringText'
import { SublistItem } from '@/Atomic/Templates/Sublist'
import { TimelineItem } from '@/Atomic/Templates/TimelineItem'
import { ActivityActionMenu } from '@/Components/Activity/ActivityActionMenu'
import { ActivityPersonAvatarGroup } from '@/Components/Activity/ActivityPersonAvatarGroup'
import { ActivityTimelineHeader } from '@/Components/Activity/ActivityTimelineHeader'
import { getActivityTypeIcon } from '@/Components/Activity/getActivityTypeIcon'
import { getRowMenu } from '@/Components/EntityTable/getRowMenu'
import { ConfirmationModal } from '@/Components/shared/modals/ConfirmationModal'
import { errorToast } from '@/Components/Toast'
import { Activity } from '@/Data/Activity/ActivityDataClass'
import { DataClassName } from '@/Data/const'
import { useDoubleClick } from '@/hooks/useDoubleClick'
import { useModalHandler } from '@/hooks/useModalHandler'
import { getDataItemTitle } from '@/utils/dataClassMap'
import { formatDateTime } from '@/utils/formatDateTime'
import { getDataItem } from '@/utils/getDataItem'
import { isString } from '@/utils/isString'
import { openDataItemInNewTab } from '@/utils/openDataItemInNewTab'
import { reportError } from '@/utils/reportError'
import { getDeleteModalSubtitle } from '@/Widgets/DXPDetails/utils/deleteModalSubtitle'
import { getDeleteModalTitle } from '@/Widgets/DXPDetails/utils/deleteModalTitle'

interface ActivitySublistItemProps {
  activity: DataItem
  flash?: boolean
  onOpenDrawer?: (activity: DataItem, startInEditMode?: boolean) => void
}

export const ActivitySublistItem: React.FC<ActivitySublistItemProps> = connect(
  ({ activity, flash, onOpenDrawer }) => {
    const { t } = useTranslation()
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [menuTrigger, setMenuTrigger] = useState<boolean>()
    const {
      isOpen: isDeleteModalOpen,
      openModal: openDeleteModal,
      closeModal: closeDeleteModal,
    } = useModalHandler()
    const title =
      (activity.get('displayTitle') as string) ||
      (activity.get('subject') as string) ||
      ''
    const note = (activity.get('note') as string) || ''
    const dueDate = activity.get('dueAt') || activity.get('endAt')
    const dueDateValue = dueDate ? formatDateTime(dueDate, 'MMM d, HH:mm') : ''
    const dueDateVariant = getDueDateVariant(activity.get('priority'))
    const originId = activity.get('origin')
    const originActivity = isString(originId)
      ? getDataItem(Activity, originId).dataItem
      : null
    const originTitle = getDataItemTitle(originActivity) || ''

    const navigateToActivity = (event: React.MouseEvent) => {
      event.stopPropagation()
      navigateTo(activity)
    }

    const openActivityInNewTab = () => {
      openDataItemInNewTab(activity)
    }

    const handleCardClick = useDoubleClick<HTMLAnchorElement>(
      () => onOpenDrawer?.(activity),
      openActivityInNewTab,
    )

    const menuItems = getRowMenu({
      dataItem: activity,
      t,
      onNavigateToClick: navigateToActivity,
      onOpenInNewTabClick: openActivityInNewTab,
      onOpenEntityCanvasClick: (event, startInEditMode) => {
        event.stopPropagation()
        onOpenDrawer?.(activity, startInEditMode)
      },
      openDeleteModal,
    })
    const deleteActivity = async () => {
      try {
        await activity.delete()
        closeDeleteModal()
      } catch (error) {
        errorToast(error as Error)
        reportError(error)
      }
    }

    const cardContents = (
      <>
        <span className="activity-sublist-item__title">{title}</span>
        <ActivityPersonAvatarGroup
          activity={activity}
          className="activity-sublist-item__contacts"
        />
        {note ? (
          <span className="activity-sublist-item__note">{note}</span>
        ) : null}
        {dueDateValue ? (
          <div className="activity-sublist-item__due-date">
            <Badge value={dueDateValue} variant={dueDateVariant} />
          </div>
        ) : null}
      </>
    )

    const originContent =
      originActivity && originTitle ? (
        <div className="activity-sublist-item__origin">
          <Icon
            icon={getActivityTypeIcon(originActivity.get('type'))}
            color="strong"
            size="sm"
          />
          <StringText
            className="activity-sublist-item__origin-title"
            wrapperClassName="activity-sublist-item__origin-title-wrapper"
            dataItem={originActivity}
            value={originTitle}
            variant="link"
          />
        </div>
      ) : null

    return (
      <>
        <SublistItem className="activity-sublist-item">
          <TimelineItem
            icon={getActivityTypeIcon(activity.get('type'))}
            header={<ActivityTimelineHeader activity={activity} />}
            origin={originContent}
          >
            <div
              className={classNames('activity-sublist-item__card', {
                'activity-sublist-item__card--menu-open': isMenuOpen,
                flash,
              })}
              onContextMenu={(event) => {
                event.preventDefault()
                event.stopPropagation()
                setMenuTrigger((currentTrigger) => !currentTrigger)
              }}
            >
              <DataItemLink
                className="activity-sublist-item__card-link"
                dataItem={activity}
                onClick={(event) => {
                  if (
                    !onOpenDrawer ||
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    event.altKey ||
                    event.button !== 0
                  ) {
                    return
                  }

                  event.preventDefault()
                  handleCardClick(event)
                }}
              >
                {cardContents}
              </DataItemLink>
              <div
                className="activity-sublist-item__actions"
                onClick={(event) => event.stopPropagation()}
              >
                <ActivityActionMenu
                  menuItems={menuItems}
                  onOpenChange={setIsMenuOpen}
                  trigger={menuTrigger}
                />
              </div>
            </div>
          </TimelineItem>
        </SublistItem>
        <ConfirmationModal
          variant="danger"
          isOpen={isDeleteModalOpen}
          close={closeDeleteModal}
          confirm={deleteActivity}
          title={t(getDeleteModalTitle(DataClassName.ACTIVITY))}
          subtitle={t(getDeleteModalSubtitle(DataClassName.ACTIVITY))}
        />
      </>
    )
  },
)

const getDueDateVariant = (priority: unknown): BadgeVariant => {
  const normalizedPriority = isString(priority) ? priority.toLowerCase() : ''

  if (normalizedPriority === 'normal' || normalizedPriority === 'psa_nrm') {
    return 'info'
  }

  if (normalizedPriority === 'high' || normalizedPriority === 'psa_hgh') {
    return 'warning'
  }

  return 'primary'
}
